import {
    createFileRoute,
    redirect,
    useNavigate,
    useSearch,
} from '@tanstack/react-router'
import { Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'

import { FIELD_CLASS } from '@/components/auth/auth-field'
import { OtpStep } from '@/components/auth/otp-step'
import { StagingTestBadge } from '@/components/staging-test-badge'
import { AUTH_ME_QUERY_KEY, meQueryFn, useAuth } from '@/hooks/use-auth'
import { authApi } from '@/lib/api'
import { APP_NAME } from '@/lib/constants'
import { m } from '@/lib/paraglide/messages'
import { posthog } from '@/lib/posthog'
import { queryClient } from '@/router'

const safeRedirect = (url?: string) => {
    if (!url || !url.startsWith('/') || url.startsWith('//')) return '/'
    return url
}

export const Route = createFileRoute('/login')({
    validateSearch: (search: Record<string, unknown>) => ({
        redirect: (search.redirect as string) || undefined,
    }),
    beforeLoad: async ({ search }) => {
        // Đã đăng nhập mà vào /login → đẩy về home (hoặc trang redirect nếu có).
        // Dùng ensureQueryData thay vì đọc cache trần: khi mở thẳng /login lúc
        // cache /auth/me chưa nạp (nhưng cookie còn hạn), vẫn phát hiện được.
        const data = await queryClient.ensureQueryData({
            queryKey: AUTH_ME_QUERY_KEY,
            queryFn: meQueryFn,
        })
        if (data?.user) {
            throw redirect({ to: safeRedirect(search.redirect) })
        }
    },
    component: LoginPage,
})

// Dùng khi server không nói rõ phải chờ bao lâu; khớp với otpResendCooldown ở
// backend (server/internal/usecase/auth/otp.go).
const DEFAULT_OTP_RESEND_SECONDS = 60

interface OtpChallengeState {
    token: string
    emailMasked: string
    resendAfterSeconds: number
}

function LoginPage() {
    const { login, verifyLoginOtp } = useAuth()
    const navigate = useNavigate()
    const { redirect: redirectTo } = useSearch({ from: '/login' })
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    // Khác null = đang ở bước 2 (nhập mã OTP).
    const [otpChallenge, setOtpChallenge] = useState<OtpChallengeState | null>(
        null,
    )

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setIsLoading(true)
        try {
            const result = await login({ username, password })
            if (result.otpRequired && result.challengeToken) {
                setOtpChallenge({
                    token: result.challengeToken,
                    emailMasked: result.emailMasked ?? '',
                    resendAfterSeconds:
                        result.resendAfterSeconds ?? DEFAULT_OTP_RESEND_SECONDS,
                })
                return
            }
            posthog.capture('user_logged_in', { username })
            void navigate({ to: safeRedirect(redirectTo) })
        } catch (err) {
            setError(err instanceof Error ? err.message : m.auth_login_failed())
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="relative min-h-screen">
            {/* Full-bleed tea-field photo, same shot the legacy admin uses.
                Served from /public so it stays out of the JS bundle and the
                browser can cache it across deploys. */}
            <div
                aria-hidden
                className="absolute inset-0 bg-white bg-cover bg-center"
                style={{ backgroundImage: "url('/login-bg.jpg')" }}
            />
            <div className="relative flex min-h-screen items-center justify-center p-4">
                <div className="w-full max-w-lg rounded-lg bg-card p-6 shadow-xl sm:p-8">
                    <div className="flex justify-center">
                        {/* Same asset the sidebar shows, so it is already in
                            cache after the first visit. Landscape lockup →
                            size by height and let the width follow. */}
                        <div className="relative">
                            <img
                                src="/logo.jpg"
                                alt={APP_NAME}
                                className="h-20 w-auto object-contain"
                            />
                            <StagingTestBadge size="lg" />
                        </div>
                    </div>
                    <hr className="my-4 border-border" />

                    <p className="text-center text-lg font-bold text-primary">
                        {m.auth_welcome_title()}
                    </p>
                    <p className="mt-1 text-center text-sm text-muted-foreground">
                        {m.auth_welcome_subtitle()}
                    </p>

                    {otpChallenge ? (
                        <OtpStep
                            emailMasked={otpChallenge.emailMasked}
                            resendAfterSeconds={otpChallenge.resendAfterSeconds}
                            error={error}
                            onVerify={async (code) => {
                                setError('')
                                try {
                                    await verifyLoginOtp(
                                        otpChallenge.token,
                                        code,
                                    )
                                    posthog.capture('user_logged_in', {
                                        username,
                                    })
                                    void navigate({
                                        to: safeRedirect(redirectTo),
                                    })
                                } catch (err) {
                                    setError(
                                        err instanceof Error
                                            ? err.message
                                            : m.auth_login_failed(),
                                    )
                                }
                            }}
                            onResend={async () => {
                                setError('')
                                try {
                                    const res = await authApi.resendOtp({
                                        challenge_token: otpChallenge.token,
                                    })
                                    // Server là nguồn sự thật cho cooldown: nó
                                    // vừa đặt lại last_sent_at nên con số cũ ở
                                    // đây đã lỗi thời.
                                    return (
                                        res.data?.resend_after_seconds ??
                                        DEFAULT_OTP_RESEND_SECONDS
                                    )
                                } catch (err) {
                                    setError(
                                        err instanceof Error
                                            ? err.message
                                            : m.auth_login_failed(),
                                    )
                                    // Báo thất bại để OtpStep giữ nguyên ô nhập
                                    // và nút "Gửi lại mã".
                                    return false as const
                                }
                            }}
                            onBack={() => {
                                // Quay lại bước mật khẩu: challenge cũ cứ để hết
                                // hạn tự nhiên, lần đăng nhập sau sẽ tạo cái mới.
                                setOtpChallenge(null)
                                setError('')
                            }}
                        />
                    ) : (
                        <form
                            onSubmit={handleSubmit}
                            className="mt-6 space-y-4"
                        >
                            <div>
                                <label
                                    htmlFor="username"
                                    className="block text-sm font-medium text-foreground"
                                >
                                    {m.auth_username()}
                                </label>
                                <input
                                    id="username"
                                    type="text"
                                    autoComplete="username"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    required
                                    placeholder={m.auth_username_placeholder()}
                                    value={username}
                                    onChange={(e) =>
                                        setUsername(e.target.value)
                                    }
                                    className={`mt-1 ${FIELD_CLASS}`}
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="password"
                                    className="block text-sm font-medium text-foreground"
                                >
                                    {m.auth_password()}
                                </label>
                                <div className="relative mt-1">
                                    <input
                                        id="password"
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        autoComplete="current-password"
                                        required
                                        placeholder={m.auth_password_placeholder()}
                                        value={password}
                                        onChange={(e) =>
                                            setPassword(e.target.value)
                                        }
                                        className={`${FIELD_CLASS} pr-10`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowPassword((v) => !v)
                                        }
                                        aria-label={
                                            showPassword
                                                ? m.auth_hide_password()
                                                : m.auth_show_password()
                                        }
                                        aria-pressed={showPassword}
                                        className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-3 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            {error && (
                                <p
                                    role="alert"
                                    className="text-sm text-destructive"
                                >
                                    {error}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-medium uppercase tracking-wide text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                            >
                                {isLoading
                                    ? m.auth_logging_in()
                                    : m.auth_login()}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    )
}
